import api from '@/api/axios';
import store from '@/stores/store';
import { calculatePermissions } from './permission';
import { hasOwnProperty } from '@/utils/utils';
import { cache } from './cacheService';
import encryptedStorage from '@/stores/encryptedStorage';
import { logError } from '@/utils/logHandling';
import {
  LocalSet,
  LocalWordSet,
  Patient,
  UserType,
  SetItem,
  Subscription,
  User,
} from '@/stores/store.types';
import { AxiosResponse } from 'axios';
import { deleteAsync, getInfoAsync } from 'expo-file-system';
import { ASSETS_DIR } from '@/contexts/DownloadAssetContext/createAssetDownloaderInstance';
import { IS_WEB } from '@/utils/platform';

const tempIdCache = [];

export const fetchMe = async (): Promise<User> => {
  try {
    const { data } = await api.get('/api/users/profile');
    return data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const fetchUserConsents = () => {
  return api
    .get(`/api/users/consents/list`)
    .then((res) => {
      store.getActions().setUserConsents(res.data);

      return res;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const updateUserEpa = async (params = { autoUpload: false }) => {
  return api.patch('/api/users/epa', {
    autoUpload: params.autoUpload,
  });
};

export const togglePatternLockOption = async (key: string, value: unknown) => {
  const data: Record<string, unknown> = {};
  data[key] = value;
  try {
    const res = await api.put('/api/users', data);
    return res.status;
  } catch {
    return 400;
  }
};

export const toggleTherapistCanSeeResults = async (value: unknown) => {
  try {
    await api.put('/api/users', value);
    return true;
  } catch {
    return false;
  }
};

export const saveMarketingChannelSelection = async (payload: {
  marketingChannelResponse?: string;
  marketingChannelSelection: string;
}) => {
  try {
    const { data } = await api.put('/api/users', payload);
    return data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const saveNewPatternLock = async (appLockPattern: string) => {
  let response: {
    hash: string;
    status: number;
  } = {
    hash: '',
    status: 400,
  };

  try {
    const { data } = await api.put<UserType>(
      '/api/users',
      { appLockPattern, patternIsNotHashed: true },
      {
        timeout: 10000,
      },
    );
    response.hash = data.appLockPattern;
    response.status = 200;
  } catch {
    response.status = 400;
  }
  return response;
};

export const checkPassword = async (password: string) => {
  try {
    const res = await api.post(
      '/api/auth/password',
      { password },
      {
        timeout: 10000,
      },
    );
    return res.status;
  } catch {
    return 400;
  }
};

export async function fetchPatientsList(stackChanges: true): Promise<{
  patients: Patient[];
  appStartResumeLock: boolean;
  eigentrainingLock: boolean;
  appLockPattern: string;
}>;

export async function fetchPatientsList(
  stackChanges?: false,
): Promise<Patient[]>;

export async function fetchPatientsList(stackChanges = false): Promise<
  | Patient[]
  | {
      patients: Patient[];
      appStartResumeLock: boolean;
      eigentrainingLock: boolean;
      appLockPattern: string;
    }
> {
  try {
    const res = await api.get(`/api/users/syncdata`, { timeout: 10000 });
    const { patients } = res.data;

    if (!stackChanges) {
      store.getActions().setDownloadPatients(patients);
      store.getActions().setPatternLockInfo({
        appStartResumeLock: res.data.appStartResumeLock,
        eigentrainingLock: res.data.eigentrainingLock,
        appLockPattern: res.data.appLockPattern,
      });
      return patients;
    } else {
      return {
        patients,
        appStartResumeLock: res.data.appStartResumeLock,
        eigentrainingLock: res.data.eigentrainingLock,
        appLockPattern: res.data.appLockPattern,
      };
    }
  } catch (error) {
    logError('Could not fetch patient list', error);
    return Promise.reject(error);
  }
}

export const getTimeStamps = async (payload: unknown) => {
  try {
    const { data } = await api.post('/api/wordsets/timestamps', payload, {
      timeout: 10000,
    });
    return data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const download = async (
  url: string,
  setId: string,
  localWordset: Record<string, any>,
  timestamps: Record<string, Date>,
) => {
  let download;
  // set exerciceClass default to word to migrate old app versions
  let exerciseClass = 'word';
  if (localWordset.exerciseClass && localWordset.exerciseClass === 'sentence') {
    exerciseClass = 'sentence';
  } else if (
    localWordset.exerciseClass &&
    localWordset.exerciseClass === 'text'
  ) {
    exerciseClass = 'text';
  }

  const wordsetNoPopulation =
    '/api/patients/' +
    localWordset.patientId +
    '/' +
    exerciseClass +
    'sets/' +
    setId +
    '/';

  //Check if timestamp exists in object
  if (typeof timestamps[setId] !== 'undefined') {
    let responseObject = {
      data: {
        updatedAt: timestamps[setId],
      },
    };

    await compareTimestampOnly(responseObject);
  } else {
    const sets = await api.get(wordsetNoPopulation, { timeout: 10000 });
    await compareTimestampOnly(sets);
  }

  async function setLocalSetStructure(setId: string, modifiedSet: unknown) {
    await encryptedStorage().setItem(setId, modifiedSet);
  }

  async function compareTimestampOnly(response: { data: { updatedAt: Date } }) {
    if (
      localWordset.sync &&
      hasOwnProperty(localWordset, 'activeWordset') &&
      localWordset.activeWordset[exerciseClass + 'set'].updatedAt ===
        response.data.updatedAt
    ) {
      if (
        !hasOwnProperty(localWordset, 'preparedSet') ||
        !hasOwnProperty(localWordset.preparedSet, 'exercises') ||
        !hasOwnProperty(localWordset, 'assets') ||
        !hasOwnProperty(localWordset, 'videosAdded') ||
        (exerciseClass !== 'text' &&
          !hasOwnProperty(localWordset, 'ablenkerFromSetJson'))
      ) {
        const preparedSet = getExercises(
          localWordset.activeWordset,
          localWordset,
        );
        localWordset.preparedSet = preparedSet;
        localWordset.videosAdded = true;
        await setLocalSetStructure(setId, localWordset);
      } else {
        addCacheFromStorage(setId);
      }
    } else {
      // download new set
      const response = await api.get(url, { timeout: 20000 });
      download = response.data;
      localWordset.activeWordset = download;
      localWordset.preparedSet = getExercises(download, localWordset);
      localWordset.videosAdded = true;
      await setLocalSetStructure(setId, localWordset);
    }
  }
};

export const downloadPatient = async (
  setId: string,
  localWordset: LocalSet,
  download: { _id: string }[],
) => {
  if (download.length === 0) {
    return;
  }
  // set exerciceClass default to word to migrate old app versions
  let exerciseClass = 'word';
  if (localWordset.exerciseClass && localWordset.exerciseClass === 'sentence') {
    exerciseClass = 'sentence';
  } else if (
    localWordset.exerciseClass &&
    localWordset.exerciseClass === 'text'
  ) {
    exerciseClass = 'text';
  }

  const activeSet = download.find((item) => item._id === setId);
  if (!activeSet) {
    return;
  }

  const activeWordset = {
    [exerciseClass + 'set']: activeSet,
  };

  localWordset.activeWordset = activeWordset;
  localWordset.preparedSet = getExercises(activeWordset, localWordset);
  localWordset.videosAdded = true;

  await encryptedStorage().setItem(setId, localWordset);
};

function addCacheFromStorage(wordsetId: string) {
  tempIdCache.push(wordsetId);
}

export async function copyExcerciseSet(
  userId: string,
  setId: string,
  set: string,
  title: string,
) {
  if (set === '' && title === '') {
    return await api.post(`/api/patients/${userId}/textsets/copy`, {
      textsetId: setId,
    });
  }
  return await api.post(
    `/api/patients/${userId}/${set}?from${title}=${setId}`,
    {},
  );
}

function getExercises(download: any, localWordset: any) {
  let exercises: any[] = [];
  // set exerciceClass default to word to migrate old app versions
  let exerciseClass = 'word';
  if (localWordset.exerciseClass && localWordset.exerciseClass === 'sentence') {
    exerciseClass = 'sentence';
  } else if (
    localWordset.exerciseClass &&
    localWordset.exerciseClass === 'text'
  ) {
    exerciseClass = 'text';
  }

  let ablenkerFromSetJson: Record<string, unknown> = {};
  localWordset.assets = [];
  const iterableStructure =
    download[exerciseClass + 'set'][exerciseClass + 'setItems'];
  iterableStructure.forEach((setItem: any) => {
    createExercises(setItem, exercises, localWordset, exerciseClass);
    // add picture to ablenkerFromSetJson Object to add Ablenker from set in Verstehen and Lesen (is setting is fromset)
    // needs to be done for words and sentences only, not for texts
    if (exerciseClass !== 'text') {
      ablenkerFromSetJson[setItem._id] = setItem[exerciseClass].pictures[0];
      localWordset.ablenkerFromSetJson = JSON.stringify(ablenkerFromSetJson);
    }
  });
  let preparedSet = {
    exercises,
    date: download[exerciseClass + 'set'].updatedAt,
  };

  localWordset.preparedSet = preparedSet;
  return preparedSet;
}

function createExercises(
  setitem: any,
  exercises: any[],
  localWordset: any,
  exerciseClass: any,
) {
  if (exerciseClass !== 'text') {
    setitem[exerciseClass].retinaImage = setitem[exerciseClass].pictures[0];
    let pathArray = setitem[exerciseClass].retinaImage.split('/');
    pathArray[6] = 't_original';
    setitem[exerciseClass].retinaImage = '';
    for (let i = 0; i < pathArray.length; i++) {
      if (i > 0) {
        setitem[exerciseClass].retinaImage += '/';
      }
      setitem[exerciseClass].retinaImage += pathArray[i];
    }
  }

  createExercise('Verstehen', setitem, exercises, exerciseClass);
  createExercise('Sprechen', setitem, exercises, exerciseClass);
  createExercise('Schreiben', setitem, exercises, exerciseClass);
  createExercise('Lesen', setitem, exercises, exerciseClass);
  cacheExercises(setitem, localWordset, exerciseClass);
}

function cacheExercises(
  setitem: SetItem,
  localWordset: LocalWordSet,
  exerciseClass: 'word' | 'sentence' | 'text',
) {
  // mark assets to download for words / sentences
  if (exerciseClass !== 'text') {
    cache.add(setitem[exerciseClass].retinaImage);
    if (exerciseClass === 'sentence') {
      cache.add(setitem[exerciseClass].audio);
    } else if (exerciseClass === 'word') {
      cache.add(setitem[exerciseClass].video);
    }
    cache.add(setitem[exerciseClass].pictures);
    cache.add(setitem.semanticPictures);
    cache.add(setitem.unrelatedPictures);
    localWordset.assets.push(
      setitem[exerciseClass].pictures,
      setitem.semanticPictures,
      setitem.unrelatedPictures,
      setitem[exerciseClass].retinaImage,
    );
    if (exerciseClass === 'sentence') {
      localWordset.assets.push(setitem[exerciseClass].audio);
    } else if (exerciseClass === 'word') {
      localWordset.assets.push(setitem[exerciseClass].video);
    }
  }
  // mark assets do download for texts
  else {
    let textLength = setitem.textLength;
    let text = setitem.text;

    if (textLength === 'kurz') {
      cache.add(text.kurzTextNormalAudioURL);
      cache.add(text.kurzTextSlowAudioURL);
      localWordset.assets.push(text.kurzTextNormalAudioURL);
      localWordset.assets.push(text.kurzTextSlowAudioURL);
    } else {
      cache.add(text.fullTextNormalAudioURL);
      cache.add(text.fullTextSlowAudioURL);
      localWordset.assets.push(text.fullTextNormalAudioURL);
      localWordset.assets.push(text.fullTextSlowAudioURL);
    }

    text.questions.forEach(function (question) {
      if (isInArray(setitem.textLength, question.questionCode)) {
        cache.add(question.questionAudioURL);
        localWordset.assets.push(question.questionAudioURL);
        question.choices.forEach(function (choice) {
          cache.add(choice.choiceAudioURL);
          localWordset.assets.push(choice.choiceAudioURL);
        });
      }
    });
  }
}

function createExercise(
  name: string,
  setitem: any,
  exercises: any[],
  exerciseClass: any,
) {
  const exercise: Record<string, any> = {
    type: name,
    wordsetitem: setitem._id,
  };

  // create exercises for word and sentences
  if (exerciseClass !== 'text') {
    exercise.word = setitem[exerciseClass].print;
    exercise[exerciseClass] = setitem[exerciseClass].print;

    if (exerciseClass === 'sentence') {
      exercise.audio = setitem[exerciseClass].audio;
      exercise.optionalWrongWords = setitem[exerciseClass].optionalWrongWords;
    } else if (exerciseClass === 'word') {
      exercise.video = setitem[exerciseClass].video;
    }
    exercise.correct = setitem[exerciseClass].pictures;
    exercise.retinaImage = setitem[exerciseClass].retinaImage;
    exercise.partsOfSpeech = setitem[exerciseClass].partsOfSpeech;

    if (name === 'Verstehen' || name === 'Lesen') {
      exercise.related = setitem.semanticPictures;
      exercise.unrelated = setitem.unrelatedPictures;
    }
  }
  // create exercises for texts
  else {
    let text = setitem.text;
    exercise.questions = [];
    if (name === 'Lesen') {
      exercise.title = text.title;
      if (setitem.textLength === 'kurz') {
        exercise.textContent = text.simpleBody;
      } else {
        exercise.textContent = text.body;
      }
      text.questions.forEach(function (question: any) {
        if (
          isInArray(name, question.modality) &&
          isInArray(setitem.textLength, question.questionCode)
        ) {
          let newQuestion: Record<string, any> = {};
          newQuestion.questionText = question.question;
          newQuestion.choices = [];
          newQuestion.questionType = question.questionType;
          if (question.questionType === 'open') {
            newQuestion.openQuestionAnswer = question.openQuestionAnswer;
          }
          question.choices.forEach(function (choice: any) {
            newQuestion.choices.push({
              isCorrect: choice.isCorrect,
              choiceText: choice.choiceText,
            });
          });
          exercise.questions.push(newQuestion);
        }
      });
    } else if (name === 'Verstehen') {
      if (setitem.textLength === 'kurz') {
        exercise.textNormalAudioURL = text.kurzTextNormalAudioURL;
        exercise.textSlowAudioURL = text.kurzTextSlowAudioURL;
      } else {
        exercise.textNormalAudioURL = text.fullTextNormalAudioURL;
        exercise.textSlowAudioURL = text.fullTextSlowAudioURL;
      }
      text.questions.forEach(function (question: any) {
        if (
          isInArray(name, question.modality) &&
          isInArray(setitem.textLength, question.questionCode)
        ) {
          let newQuestion: any = {};
          newQuestion.questionAudio = question.questionAudioURL;
          newQuestion.choices = [];
          newQuestion.questionType = question.questionType;
          if (question.questionType === 'open') {
            newQuestion.openQuestionAnswer = question.openQuestionAnswer;
          }
          question.choices.forEach(function (choice: any) {
            newQuestion.choices.push({
              isCorrect: choice.isCorrect,
              choiceAudio: choice.choiceAudioURL,
            });
          });
          exercise.questions.push(newQuestion);
        }
      });
    } else if (name === 'Schreiben') {
      exercise.textTitle = text.title;
      if (setitem.textLength === 'kurz') {
        exercise.textContent = text.simpleBody;
        exercise.textNormalAudioURL = text.kurzTextNormalAudioURL;
        exercise.textSlowAudioURL = text.kurzTextSlowAudioURL;
      } else {
        exercise.textContent = text.body;
        exercise.textNormalAudioURL = text.fullTextNormalAudioURL;
        exercise.textSlowAudioURL = text.fullTextSlowAudioURL;
      }
    } else if (name === 'Sprechen') {
      exercise.title = text.title;
      if (setitem.textLength === 'kurz') {
        exercise.textNormalAudioURL = text.kurzTextNormalAudioURL;
        exercise.textSlowAudioURL = text.kurzTextSlowAudioURL;
      } else {
        exercise.textNormalAudioURL = text.fullTextNormalAudioURL;
        exercise.textSlowAudioURL = text.fullTextSlowAudioURL;
      }
      if (setitem.textLength === 'kurz') {
        exercise.textContent = text.simpleBody;
      } else {
        exercise.textContent = text.body;
      }
      text.questions.forEach(function (question: any) {
        if (
          isInArray(name, question.modality) &&
          isInArray(setitem.textLength, question.questionCode)
        ) {
          let newQuestion: any = {};
          newQuestion.questionAudio = question.questionAudioURL;
          newQuestion.choices = [];
          newQuestion.questionText = question.question;
          if (question.questionType === 'open') {
            newQuestion.openQuestionAnswer = question.openQuestionAnswer;
          }
          exercise.questions.push(newQuestion);
        }
      });
    }
  }

  exercise.exerciseClass = exerciseClass;
  exercises.push(exercise);
}

function isInArray(value: string, array: unknown[]) {
  return array.indexOf(value) > -1;
}

export const getUser = async (): Promise<UserType> => {
  try {
    const { data } = await api.get('/api/users/profile');
    return data;
  } catch (error) {
    logError('Could not get logged in user', error);
    return Promise.reject(error);
  }
};

export const deleteUser = () => {
  return api
    .delete(`/api/users/delete`)
    .then((res) => {
      return res;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const changePassword = (payload: unknown) => {
  return api
    .post(`/api/users/password`, payload)
    .then((res) => {
      return res;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

type ResetPasswordPayload = {
  newPassword: string;
  verifyPassword: string;
};

export const resetPassword = (payload: ResetPasswordPayload, token: string) => {
  return api
    .post(`/api/auth/reset/${token}`, payload)
    .then((res) => {
      return res;
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const getPermissions = async (
  stackChanges = false,
  useCachedPermissions = false,
): Promise<{
  subscriptions: Subscription[];
  permissions: Subscription;
}> => {
  let res: AxiosResponse | null = null;

  try {
    if (!useCachedPermissions) {
      res = await api.get(`/api/permissions`).catch(() => null);
    }
    // Something went wrong during the fetch, we fallback to the value in the store. (ie: offline)
    if (res === null) {
      return {
        subscriptions: store.getState().subscriptions,
        permissions: store.getState().permissions,
      };
    }

    if (!res?.data.subscriptions) {
      // In this case we fallback to the default value from the calculatePermissions function
      const permissions = calculatePermissions();
      return { subscriptions: [{ plan: permissions.plan }], permissions };
    } else {
      if (stackChanges) {
        const permissions = calculatePermissions();
        store.getActions().setSubscriptions(res.data.subscriptions);
        store.getActions().setPermissions(permissions);
        return { subscriptions: res.data.subscriptions, permissions };
      } else {
        const permissions = calculatePermissions(res.data.subscriptions);
        store.getActions().setSubscriptions(res.data.subscriptions);
        store.getActions().setPermissions(permissions);
        return { subscriptions: res.data.subscriptions, permissions };
      }
    }
  } catch (error) {
    logError('Could not fetch permissions', error);
    return {
      subscriptions: store.getState().subscriptions,
      permissions: store.getState().permissions,
    };
  }
};

export const localLogoutUser = async (accountWasDeleted = false) => {
  // store lastLoginEmail in memory, to transfer it to the new encrypted storage initialized with a new encryption key
  const {
    lastLoginEmail,
    isAuthenticated,
    isTherapistDemo,
    therapistWebAppLogin,
  } = store.getState();

  await encryptedStorage().destroyEncryptionKey();
  if (isAuthenticated) {
    if (!IS_WEB) {
      const dirInfo = await getInfoAsync(ASSETS_DIR);
      if (dirInfo.exists) {
        await deleteAsync(ASSETS_DIR, { idempotent: true });
      }
    }

    const {
      reset,
      setLastIdleDate,
      setLastLoginEmail,
      setTherapistWebAppLogin,
      setIsAuthenticated,
    } = store.getActions();

    if (therapistWebAppLogin) {
      setTherapistWebAppLogin(false);
      window.location.href = window.location.href.split('?')[0];
    }

    setIsAuthenticated(false);
    reset();
    await store.persist.clear();
    await encryptedStorage().clear();
    if (accountWasDeleted) {
      await encryptedStorage().setItem(
        'accountWasDeleted',
        JSON.stringify(true),
      );
    }
    setLastIdleDate(null);
    if (!isTherapistDemo) {
      setLastLoginEmail(lastLoginEmail);
    }
  }
};

export type LicenseStatus = {
  type: 'diga' | 'selfpay';
  expireAt: string;
  daysLeft: number;
};

export const getLicenseStatus = async (): Promise<LicenseStatus | null> => {
  try {
    const res = await api.get(`/api/users/license-status`);
    return res.data?.license;
  } catch (error) {
    logError('Could not get license status', error);
    return null;
  }
};
